<template>
  <div class="bg-white rounded-lg shadow-md">
    <!-- Header -->
    <div class="p-4 border-b border-secondary-100">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <div class="w-8 h-8 bg-securdia-blue/10 rounded-lg flex items-center justify-center">
            <svg class="w-4 h-4 text-securdia-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
            </svg>
          </div>
          <div>
            <h3 class="text-sm font-bold text-secondary-800">{{ $t('sales_dashboard.seller_analysis') }}</h3>
            <p class="text-xs text-secondary-500">
              {{ activeSellers.length }} {{ $t('sales_dashboard.active_sellers_count') }} • {{ formatCompactCurrency(totalRevenue) }} {{ $t('sales_dashboard.total_revenue_label_short') }}
            </p>
          </div>
        </div>
        
        <!-- View tabs -->
        <div class="flex bg-secondary-100 rounded-lg p-1">
          <button 
            v-for="tab in viewTabs"
            :key="tab.id"
            @click="activeView = tab.id"
            :class="activeView === tab.id ? 'bg-white text-securdia-blue shadow-sm' : 'text-secondary-600 hover:text-secondary-800'"
            class="px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
          >
            {{ tab.label }}
          </button>
        </div>
      </div>
    </div>

    <!-- Content -->
    <div class="p-4">
      <!-- Overview / Diagram View -->
      <div v-if="activeView === 'diagram'">
        <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          <div 
            v-for="seller in sortedSellers" 
            :key="seller.id"
            @click="selectSeller(seller)"
            class="relative p-3 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md"
            :class="[
              selectedSeller?.id === seller.id ? 'border-securdia-blue bg-primary-50' : 'border-secondary-200 hover:border-securdia-blue/50',
              getMarginBorderClass(seller.avgMargin)
            ]"
          >
            <!-- Seller avatar -->
            <div class="flex items-center gap-2 mb-2">
              <div 
                class="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold"
                :style="{ backgroundColor: getSellerColor(seller.id) }"
              >
                {{ getInitials(seller.name) }}
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-semibold text-secondary-800 truncate">{{ seller.name }}</p>
              </div>
            </div>
            
            <!-- Stats -->
            <div class="space-y-1">
              <div class="flex justify-between text-xs">
                <span class="text-secondary-500">{{ $t('sales_dashboard.revenue_label') }}</span>
                <span class="font-semibold text-secondary-700">{{ formatCompactCurrency(seller.totalRevenue) }}</span>
              </div>
              <div class="flex justify-between text-xs">
                <span class="text-secondary-500">{{ $t('sales_dashboard.margin_label') }}</span>
                <span 
                  class="font-semibold"
                  :class="getMarginTextClass(seller.avgMargin)"
                >
                  {{ seller.avgMargin?.toFixed(1) || 0 }}%
                </span>
              </div>
            </div>
            
            <!-- Margin indicator bar -->
            <div class="mt-2 h-1.5 bg-secondary-200 rounded-full overflow-hidden">
              <div 
                class="h-full rounded-full transition-all"
                :class="getMarginBarClass(seller.avgMargin)"
                :style="{ width: Math.min(seller.avgMargin || 0, 100) + '%' }"
              ></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Details View -->
      <div v-if="activeView === 'detaljer'">
        <!-- Selected seller info or prompt -->
        <div v-if="selectedSeller" class="space-y-4">
          <!-- Seller header with clear button -->
          <div class="flex items-center justify-between p-3 bg-secondary-50 rounded-lg">
            <div class="flex items-center gap-3">
              <div 
                class="w-12 h-12 rounded-full flex items-center justify-center text-white text-lg font-bold"
                :style="{ backgroundColor: getSellerColor(selectedSeller.id) }"
              >
                {{ getInitials(selectedSeller.name) }}
              </div>
              <div>
                <h4 class="text-lg font-bold text-secondary-800">{{ selectedSeller.name }}</h4>
                <p class="text-xs text-secondary-500">{{ $t('sales_dashboard.active_since') }} {{ formatDate(selectedSeller.activeSince) }}</p>
              </div>
            </div>
            <button 
              @click="selectedSeller = null"
              class="px-3 py-1.5 text-xs font-medium text-secondary-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            >
              {{ $t('sales_dashboard.clear') }}
            </button>
          </div>

          <!-- Stats grid -->
          <div class="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <!-- Total -->
            <div class="bg-secondary-50 rounded-lg p-4">
              <p class="text-xs text-secondary-500 mb-1">{{ $t('sales_dashboard.total_short') }}</p>
              <p class="text-xl font-bold text-secondary-800">{{ formatCompactCurrency(selectedSeller.totalRevenue) }}</p>
            </div>
            
            <!-- Cash -->
            <div class="bg-securdia-orange/5 rounded-lg p-4 border border-securdia-orange/20">
              <p class="text-xs text-secondary-500 mb-1">{{ $t('sales_dashboard.cash_label') }}</p>
              <p class="text-lg font-bold text-securdia-orange">{{ selectedSeller.cashCount || 0 }} {{ $t('sales_dashboard.pieces_unit') }}</p>
              <p class="text-xs text-secondary-600">{{ formatCompactCurrency(selectedSeller.cashRevenue) }}</p>
            </div>
            
            <!-- Subscriptions -->
            <div class="bg-securdia-blue/5 rounded-lg p-4 border border-securdia-blue/20">
              <p class="text-xs text-secondary-500 mb-1">{{ $t('sales_dashboard.subscriptions') }}</p>
              <p class="text-lg font-bold text-securdia-blue">{{ selectedSeller.subscriptionCount || 0 }} {{ $t('sales_dashboard.pieces_unit') }}</p>
              <p class="text-xs text-secondary-600">{{ formatCompactCurrency(selectedSeller.mrr) }}{{ $t('sales_dashboard.per_month_short') }}</p>
            </div>
            
            <!-- Margin -->
            <div 
              class="rounded-lg p-4 border"
              :class="getMarginBgClass(selectedSeller.avgMargin)"
            >
              <p class="text-xs text-secondary-500 mb-1">{{ $t('sales_dashboard.avg_margin') }}</p>
              <p 
                class="text-xl font-bold"
                :class="getMarginTextClass(selectedSeller.avgMargin)"
              >
                {{ selectedSeller.avgMargin?.toFixed(1) || 0 }}%
              </p>
              <p v-if="selectedSeller.lowMarginCount > 0" class="text-xs text-red-500">
                {{ selectedSeller.lowMarginCount }} {{ $t('sales_dashboard.under_40_percent') }}
              </p>
            </div>

            <!-- Contracts -->
            <div class="bg-secondary-50 rounded-lg p-4">
              <p class="text-xs text-secondary-500 mb-1">{{ $t('sales_dashboard.total_contracts_label') }}</p>
              <p class="text-xl font-bold text-secondary-800">{{ selectedSeller.totalContracts || 0 }}</p>
            </div>
          </div>
        </div>

        <!-- No selection prompt -->
        <div v-else class="text-center py-12">
          <svg class="w-12 h-12 mx-auto text-secondary-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
          </svg>
          <p class="text-secondary-500">{{ $t('sales_dashboard.select_seller_prompt') }}</p>
        </div>
      </div>

      <!-- Compare View -->
      <div v-if="activeView === 'jamfor'">
        <!-- Seller selectors -->
        <div class="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label class="block text-xs font-medium text-secondary-600 mb-1">{{ $t('sales_dashboard.seller_1') }}</label>
            <select 
              v-model="compareSeller1"
              class="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-securdia-blue/30"
            >
              <option :value="null">{{ $t('sales_dashboard.select_seller_dropdown') }}</option>
              <option 
                v-for="seller in activeSellers" 
                :key="seller.id" 
                :value="seller"
                :disabled="compareSeller2?.id === seller.id"
              >
                {{ seller.name }}
              </option>
            </select>
          </div>
          <div>
            <label class="block text-xs font-medium text-secondary-600 mb-1">{{ $t('sales_dashboard.seller_2') }}</label>
            <select 
              v-model="compareSeller2"
              class="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-securdia-blue/30"
            >
              <option :value="null">{{ $t('sales_dashboard.select_seller_dropdown') }}</option>
              <option 
                v-for="seller in activeSellers" 
                :key="seller.id" 
                :value="seller"
                :disabled="compareSeller1?.id === seller.id"
              >
                {{ seller.name }}
              </option>
            </select>
          </div>
        </div>

        <!-- Comparison table -->
        <div v-if="compareSeller1 && compareSeller2" class="border border-secondary-200 rounded-lg overflow-hidden">
          <table class="w-full text-sm">
            <thead class="bg-secondary-50">
              <tr>
                <th class="px-4 py-3 text-left text-xs font-semibold text-secondary-600">{{ $t('sales_dashboard.metric') }}</th>
                <th class="px-4 py-3 text-center">
                  <div class="flex items-center justify-center gap-2">
                    <div 
                      class="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                      :style="{ backgroundColor: getSellerColor(compareSeller1.id) }"
                    >
                      {{ getInitials(compareSeller1.name) }}
                    </div>
                    <span class="text-xs font-semibold text-secondary-700">{{ compareSeller1.name }}</span>
                  </div>
                </th>
                <th class="px-4 py-3 text-center">
                  <div class="flex items-center justify-center gap-2">
                    <div 
                      class="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                      :style="{ backgroundColor: getSellerColor(compareSeller2.id) }"
                    >
                      {{ getInitials(compareSeller2.name) }}
                    </div>
                    <span class="text-xs font-semibold text-secondary-700">{{ compareSeller2.name }}</span>
                  </div>
                </th>
                <th class="px-4 py-3 text-center text-xs font-semibold text-secondary-600">{{ $t('sales_dashboard.difference') }}</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-secondary-100">
              <tr v-for="metric in comparisonMetrics" :key="metric.key" class="hover:bg-secondary-50">
                <td class="px-4 py-3 text-secondary-700 font-medium">{{ metric.label }}</td>
                <td class="px-4 py-3 text-center font-semibold text-secondary-800">
                  {{ metric.format(compareSeller1[metric.key]) }}
                </td>
                <td class="px-4 py-3 text-center font-semibold text-secondary-800">
                  {{ metric.format(compareSeller2[metric.key]) }}
                </td>
                <td class="px-4 py-3 text-center">
                  <span 
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
                    :class="getDiffClass(compareSeller1[metric.key], compareSeller2[metric.key], metric.higherIsBetter)"
                  >
                    <svg v-if="compareSeller1[metric.key] > compareSeller2[metric.key]" class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18"></path>
                    </svg>
                    <svg v-else-if="compareSeller1[metric.key] < compareSeller2[metric.key]" class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
                    </svg>
                    {{ metric.formatDiff(Math.abs(compareSeller1[metric.key] - compareSeller2[metric.key])) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- No selection prompt -->
        <div v-else class="text-center py-12 border-2 border-dashed border-secondary-200 rounded-lg">
          <svg class="w-12 h-12 mx-auto text-secondary-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"></path>
          </svg>
          <p class="text-secondary-500">{{ $t('sales_dashboard.select_two_sellers') }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SellerAnalysisAdvanced',
  props: {
    salespersons: {
      type: Array,
      default: () => []
    },
    contracts: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      activeView: 'diagram',
      selectedSeller: null,
      compareSeller1: null,
      compareSeller2: null
    };
  },
  computed: {
    viewTabs() {
      return [
        { id: 'diagram', label: this.$t('sales_dashboard.diagram') },
        { id: 'detaljer', label: this.$t('sales_dashboard.details') },
        { id: 'jamfor', label: this.$t('sales_dashboard.compare') }
      ];
    },
    comparisonMetrics() {
      return [
        { key: 'totalRevenue', label: this.$t('sales_dashboard.total_revenue_compare'), format: this.formatCompactCurrency, formatDiff: this.formatCompactCurrency, higherIsBetter: true },
        { key: 'totalContracts', label: this.$t('sales_dashboard.total_contracts_compare'), format: v => `${v || 0} st`, formatDiff: v => `${v} st`, higherIsBetter: true },
        { key: 'cashCount', label: this.$t('sales_dashboard.cash_deals'), format: v => `${v || 0} st`, formatDiff: v => `${v} st`, higherIsBetter: true },
        { key: 'cashRevenue', label: this.$t('sales_dashboard.cash_turnover'), format: this.formatCompactCurrency, formatDiff: this.formatCompactCurrency, higherIsBetter: true },
        { key: 'subscriptionCount', label: this.$t('sales_dashboard.rental_contracts_label'), format: v => `${v || 0} st`, formatDiff: v => `${v} st`, higherIsBetter: true },
        { key: 'mrr', label: 'MRR', format: this.formatCompactCurrency, formatDiff: this.formatCompactCurrency, higherIsBetter: true },
        { key: 'avgMargin', label: this.$t('sales_dashboard.avg_margin'), format: v => `${(v || 0).toFixed(1)}%`, formatDiff: v => `${v.toFixed(1)}%`, higherIsBetter: true }
      ];
    },
    activeSellers() {
      // If salespersons already have stats from API, map to expected format
      if (this.salespersons.length > 0 && this.salespersons[0].totalRevenue !== undefined) {
        return this.salespersons
          .filter(s => (s.contractCount || 0) > 0)
          .map(s => ({
            ...s,
            totalContracts: s.contractCount,
            avgMargin: s.marginPercent || 0,
            cashCount: s.cashSalesCount || 0
          }));
      }
      
      // Otherwise, calculate stats from contracts
      return this.salespersons.map(seller => {
        const sellerId = seller.id || seller._id;
        const sellerContracts = this.contracts.filter(c => 
          c.sellerId === sellerId || c.seller_id === sellerId || 
          c.salesperson_id === sellerId || c.seller === seller.name
        );
        
        const isRentalCategory = (cat) => ['rental', 'financed', 'subscription'].includes(cat);
        
        const cashContracts = sellerContracts.filter(c => !isRentalCategory(c.category));
        const rentalContracts = sellerContracts.filter(c => isRentalCategory(c.category));
        
        const totalRevenue = sellerContracts.reduce((sum, c) => 
          sum + (c.monthlyValue || 0) * 36 + (c.cashValue || c.total_value || 0), 0);
        const cashRevenue = cashContracts.reduce((sum, c) => sum + (c.cashValue || c.total_value || 0), 0);
        const mrr = rentalContracts.reduce((sum, c) => sum + (c.monthlyValue || c.mrr || 0), 0);
        
        // Use margin from seller stats or calculate from contracts
        const avgMargin = seller.marginPercent || seller.avgMargin || seller.margin || 45; // Default to 45%
        const lowMarginCount = 0;
        
        return {
          ...seller,
          id: sellerId,
          totalRevenue,
          cashRevenue,
          mrr,
          cashCount: cashContracts.length,
          subscriptionCount: rentalContracts.length,
          totalContracts: sellerContracts.length,
          avgMargin,
          lowMarginCount,
          activeSince: seller.created_at || seller.activeSince
        };
      }).filter(s => s.totalContracts > 0);
    },
    sortedSellers() {
      return [...this.activeSellers].sort((a, b) => b.totalRevenue - a.totalRevenue);
    },
    totalRevenue() {
      return this.activeSellers.reduce((sum, s) => sum + s.totalRevenue, 0);
    }
  },
  methods: {
    selectSeller(seller) {
      this.selectedSeller = seller;
      this.activeView = 'detaljer';
    },
    getInitials(name) {
      if (!name) return '?';
      return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    },
    getSellerColor(id) {
      const colors = ['#1A8DE9', '#F29100', '#22C55E', '#8B5CF6', '#EC4899', '#14B8A6', '#F59E0B', '#6366F1'];
      const index = typeof id === 'string' ? id.charCodeAt(0) % colors.length : id % colors.length;
      return colors[index];
    },
    getMarginTextClass(margin) {
      if (margin >= 50) return 'text-green-600';
      if (margin >= 40) return 'text-securdia-blue';
      if (margin >= 25) return 'text-securdia-orange';
      return 'text-red-500';
    },
    getMarginBarClass(margin) {
      if (margin >= 50) return 'bg-green-500';
      if (margin >= 40) return 'bg-securdia-blue';
      if (margin >= 25) return 'bg-securdia-orange';
      return 'bg-red-500';
    },
    getMarginBorderClass(margin) {
      if (margin >= 50) return 'border-l-4 border-l-green-500';
      if (margin >= 40) return 'border-l-4 border-l-securdia-blue';
      if (margin >= 25) return 'border-l-4 border-l-securdia-orange';
      return 'border-l-4 border-l-red-500';
    },
    getMarginBgClass(margin) {
      if (margin >= 50) return 'bg-green-50 border-green-200';
      if (margin >= 40) return 'bg-blue-50 border-blue-200';
      if (margin >= 25) return 'bg-orange-50 border-orange-200';
      return 'bg-red-50 border-red-200';
    },
    getDiffClass(val1, val2, higherIsBetter) {
      if (val1 === val2) return 'bg-secondary-100 text-secondary-600';
      const isFirstBetter = higherIsBetter ? val1 > val2 : val1 < val2;
      return isFirstBetter ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700';
    },
    generateMockMonthlyData() {
      const months = [];
      for (let i = 0; i < 12; i++) {
        months.push({
          label: ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'][i],
          revenue: Math.floor(Math.random() * 500000) + 100000
        });
      }
      return months;
    },
    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short', year: 'numeric' });
    },
    formatCompactCurrency(value) {
      if (!value && value !== 0) return '-';
      if (Math.abs(value) >= 1000000) {
        return `${(value / 1000000).toLocaleString('sv-SE', { maximumFractionDigits: 1 })} M kr`;
      }
      if (Math.abs(value) >= 1000) {
        return `${(value / 1000).toLocaleString('sv-SE', { maximumFractionDigits: 0 })} k kr`;
      }
      return new Intl.NumberFormat('sv-SE', {
        style: 'currency',
        currency: 'SEK',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    }
  }
};
</script>
